import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  ImpactPerFacility,
  useFacilityImpactPerFacility,
} from "@/pages/dashboard/hooks/useFacilityImpact";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { useEffect } from "react";
import { FacilityImpactDetailMode } from "./FacilityImpactSideBySide";

export default function FacilityImpactPerFacility({
  mode,
  metric,
}: {
  mode: FacilityImpactDetailMode;
  metric: ImpactMetric;
}) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 10 },
      defaultSort: {
        key: "name",
        order: "ascend",
      },
    });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const formatCell = (value: number) => {
    const total = data?.total_impact[metric.key];

    if (mode === "Absolute") {
      return metric.formatter(value);
    } else {
      return formatPercentage(!total ? NaN : value / total);
    }
  };

  const columns: Column<ImpactPerFacility>[] = [
    {
      label: "Facility",
      key: "name",
      sortable: true,
      searchable: true,
      maxWidth: "2fr",
      renderer: (record) => (
        <CrossReference to={`/facilities/${record.id}`}>{record.name}</CrossReference>
      ),
    },
    {
      label: "Electricity",
      key: "electricity_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["electricity"][metric.key]),
    },
    {
      label: "Heat",
      key: "heat_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["heat"][metric.key]),
    },
    {
      label: "Water",
      key: "water_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["water"][metric.key]),
    },
    {
      label: "Cooling",
      key: "cooling_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["cooling"][metric.key]),
    },
    {
      label: "Residual waste",
      key: "residual_waste_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["residual_waste"][metric.key]),
    },
    {
      label: "Consumable",
      key: "consumable_" + metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["consumable"][metric.key]),
    },
    {
      label: "Share",
      key: "share",
      maxWidth: "1fr",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record.impact["total"][metric.key]}
            value={data?.total_impact[metric.key]}
          />
        );
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record, key) => formatCell(record.impact["total"][metric.key]),
    },
  ];

  const { data, isLoading, isFetching } = useFacilityImpactPerFacility({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  return (
    <Table
      type="grey"
      hideRaster={true}
      filter={filter}
      sort={sort}
      search={search}
      columns={columns}
      data={data?.facilities}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      totalRows={data?.total_rows}
      rowKey={"id"}
    />
  );
}
